import { Copy, Plus, Trash2 } from "lucide-react";
import {
  CheckboxField,
  InputField,
  SelectField,
  TextAreaField,
} from "./DesignRequestFormV3";
import { configuredOptions } from "./IntakeFormOptionUtils";

const CONVERSION_HELP = "A HARD conversion is a revised graphic with a significant change that CANNOT be mixed with old inventories. If you select HARD, you are directing Ball to stop producing this graphic by the selected date and to invoice any existing inventories.";
const SYMBOL_HELP = "Artwork files requiring a separate set-up/version such as for Kosher or Deposit need to be submitted as separate graphic projects. If there is no special symbol requirement, select None.";

const contactSampleFields = (form) => ({
  sample_recipient_name: form.contact_name || "",
  sample_recipient_email: form.contact_email || "",
  sample_ship_to_street: form.contact_street || "",
  sample_ship_to_city: form.contact_city || "",
  sample_ship_to_state: form.contact_state || "",
  sample_ship_to_zip: form.contact_zip || "",
});

export const DesignRequestSkuSection = ({
  form,
  skuItems,
  options,
  selectedManufacturerType,
  updateSku,
  handleDesignTypeChange,
  duplicateSku,
  removeSku,
  addSku,
}) => (
  <>
    <div className="dof-section-title">SKU DETAILS</div>
    <div className="dof-note" style={{ marginBottom: "1rem" }}>
      <div className="dof-note-title">MULTIPLE SKUS</div>
      <div className="dof-note-text">Duplicate a SKU to carry the shared information forward, then change only what differs.</div>
    </div>

    {skuItems.map((sku, index) => {
      let insideVarnishOptions = configuredOptions(options, "inside_varnish", sku.inside_varnish);
      if (selectedManufacturerType === "ball") {
        insideVarnishOptions = insideVarnishOptions.filter((item) => String(item.value).toUpperCase() === "BPANI");
      }

      const contactFields = contactSampleFields(form || {});
      const sampleSameAsContact = !!sku.sample_same_as_contact;
      const sampleValues = sampleSameAsContact ? contactFields : sku;

      return (
        <div key={index} className="dof-contact-sub" style={{ marginBottom: "1rem" }}>
          <div className="dof-sku-heading-row">
            <h4 className="dof-contact-heading">SKU {index + 1}</h4>
            <div className="dof-sku-actions">
              <button type="button" onClick={() => duplicateSku(index)} className="dof-back-btn"><Copy size={14} /> Duplicate SKU</button>
              <button type="button" onClick={() => removeSku(index)} disabled={skuItems.length === 1} className="dof-back-btn" style={{ opacity: skuItems.length === 1 ? 0.5 : 1 }}><Trash2 size={14} /> Remove</button>
            </div>
          </div>

          <div className="dof-prod-grid">
            <InputField label="SKU Name" value={sku.sku_name} onChange={(value) => updateSku(index, "sku_name", value)} />
            <InputField label="Customer Part Number" value={sku.sku_number} onChange={(value) => updateSku(index, "sku_number", value)} />
            <SelectField label="Design Type" value={sku.design_type} onChange={(value) => handleDesignTypeChange(index, value)} required options={configuredOptions(options, "design_type", sku.design_type)} />
            {sku.design_type === "Revision" && (
              <>
                <SelectField label="Conversion Type" helpText={CONVERSION_HELP} value={sku.conversion_type} onChange={(value) => updateSku(index, "conversion_type", value)} required options={configuredOptions(options, "conversion_type", sku.conversion_type)} />
                <InputField label="Conversion Date" type="date" value={sku.conversion_date} onChange={(value) => updateSku(index, "conversion_date", value)} required />
                <InputField label="Previous Vendor Item / Design Number" value={sku.previous_vendor_item_number} onChange={(value) => updateSku(index, "previous_vendor_item_number", value)} />
              </>
            )}
            <InputField label="UPC" value={sku.upc_code} onChange={(value) => updateSku(index, "upc_code", value)} />
            <InputField label="Number of Colors" value={sku.number_of_colors} onChange={(value) => updateSku(index, "number_of_colors", value)} />
            <SelectField label="Pasteurization" value={sku.pasteurization_range} onChange={(value) => updateSku(index, "pasteurization_range", value)} options={configuredOptions(options, "pasteurization", sku.pasteurization_range)} />
            {selectedManufacturerType === "ball" && <SelectField label="Symbol" helpText={SYMBOL_HELP} value={sku.symbol} onChange={(value) => updateSku(index, "symbol", value)} options={configuredOptions(options, "symbol", sku.symbol)} />}
            <SelectField label="Specialty Inks" value={sku.specialty_ink_type} onChange={(value) => updateSku(index, "specialty_ink_type", value)} options={configuredOptions(options, "specialty_ink", sku.specialty_ink_type)} />
            {sku.specialty_ink_type === "Other" && <InputField label="Other Specialty Ink" value={sku.specialty_ink_other} onChange={(value) => updateSku(index, "specialty_ink_other", value)} />}
            <SelectField label="Overvarnish" value={sku.overvarnish_type} onChange={(value) => updateSku(index, "overvarnish_type", value)} options={configuredOptions(options, "overvarnish", sku.overvarnish_type)} />
            <SelectField label="Inside Varnish" value={sku.inside_varnish} onChange={(value) => updateSku(index, "inside_varnish", value)} required options={insideVarnishOptions} />
          </div>

          <div className="dof-mfg-note" style={{ padding: "10px 12px", margin: "8px 0 14px" }}>
            Select the appropriate overvarnish and inside varnish for the chosen manufacturer.
          </div>

          <div className="dof-checks-stack">
            <CheckboxField label="Pilot Requested" checked={!!sku.pilot_requested} onChange={(checked) => updateSku(index, "pilot_requested", checked)} />
            <CheckboxField label="Reference / Collateral Sent" checked={!!sku.reference_collateral_sent} onChange={(checked) => updateSku(index, "reference_collateral_sent", checked)} />
            <CheckboxField label="Requesting Drawdowns" checked={!!sku.requesting_drawdowns} onChange={(checked) => updateSku(index, "requesting_drawdowns", checked)} />
          </div>

          <div className="dof-conditional-panel">
            <div className="dof-conditional-title">SAMPLE SHIP-TO INFORMATION</div>
            <CheckboxField
              label="Same as contact information above"
              checked={sampleSameAsContact}
              onChange={(checked) => {
                Object.entries(checked ? contactFields : { sample_same_as_contact: false }).forEach(([field, value]) => {
                  updateSku(index, field, value);
                });
                updateSku(index, "sample_same_as_contact", checked);
              }}
            />
            <div className="dof-prod-grid">
              <InputField label="Sample Recipient Name" value={sampleValues.sample_recipient_name || ""} onChange={(value) => updateSku(index, "sample_recipient_name", value)} />
              <InputField label="Sample Recipient Email" type="email" value={sampleValues.sample_recipient_email || ""} onChange={(value) => updateSku(index, "sample_recipient_email", value)} />
              <InputField label="Street Address" value={sampleValues.sample_ship_to_street || ""} onChange={(value) => updateSku(index, "sample_ship_to_street", value)} />
              <InputField label="City" value={sampleValues.sample_ship_to_city || ""} onChange={(value) => updateSku(index, "sample_ship_to_city", value)} />
              <InputField label="State" value={sampleValues.sample_ship_to_state || ""} onChange={(value) => updateSku(index, "sample_ship_to_state", value)} />
              <InputField label="ZIP" value={sampleValues.sample_ship_to_zip || ""} onChange={(value) => updateSku(index, "sample_ship_to_zip", value)} />
            </div>
          </div>

          <TextAreaField label="SKU Notes" value={sku.sku_notes} onChange={(value) => updateSku(index, "sku_notes", value)} />
        </div>
      );
    })}

    <div style={{ textAlign: "center", margin: "1rem 0 2rem" }}>
      <button type="button" onClick={addSku} className="dof-back-btn"><Plus size={14} /> Add SKU</button>
    </div>
  </>
);
